import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';
import Notice from '../Notice/Notice';
import createFakeEvent from '../../lib/createFakeEvent';

/**
 * `Checkbox` is an extension of the HTML `input` element of type "checkbox". The component accepts all properties
 * supported by the standard checkbox element.
 *
 * Checkboxes should be used over {@link Switch} components when the toggling state does **NOT** have an immediate effect,
 * e.g. when the user has to click a **Save** button to save the toggle. If the toggle is automatically saved, use a
 * {@link Switch} component instead.
 *
 * `Checkbox` also supports an indeterminate state by setting *indeterminate* to `true`. Clicking on an indeterminate
 * checkbox will always set the state to *checked*.
 *
 * An optional error, warning, success or info {@link Notice} can be displayed below the checkbox.
 *
 * @property {boolean} checked If set to `true`, this checkbox will be checked. Defaults to `false`.
 * @property {node} children Custom children that will be inserted into the container that wraps the checkbox HTML
 * element.
 * @property {string} className The class name of the component. Will be appended to "core-checkbox".
 * @property {boolean} disabled The user cannot change the state of this checkbox when this prop is set to `true`.
 * @property {node} errorMessage An error message that will be shown below the input field. Defaults to no error message.
 * @property {string} id The ID of the checkbox element.
 * @property {boolean} indeterminate If set to `true`, the state of this component will be indeterminate and the toggle
 * will be displayed half checked icon. You can use this state to represent a group of states where all of the states do
 * not have the same value.
 * @property {node} infoMessage A message that will be shown below the input field. Defaults to no message.
 * @property {string} label The label of this component.
 * @property {function} onChange The callback function that will be called whenever the user has changed the state of this
 * component.
 * @property {node} successMessage A message that will be shown below the input field. Defaults to no message.
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 *
 * @see Notice, RadioButton, Switch
 */
export default class Checkbox extends PureComponent {

  static displayName = 'Checkbox';

  static propTypes = {
    checked: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    id: PropTypes.string,
    indeterminate: PropTypes.bool,
    infoMessage: PropTypes.node,
    label: PropTypes.string,
    onChange: PropTypes.func,
    successMessage: PropTypes.node,
    warningMessage: PropTypes.node,
  };

  onChange = e => {
    const { checked, indeterminate, onChange } = this.props;

    if (onChange) {
      const state = indeterminate ? true : !checked;

      onChange(createFakeEvent(state, e));
    }
  };

  render() {
    const { children, className, errorMessage, id, indeterminate, infoMessage, label, onClearNotice, successMessage, warningMessage, ...rest } = this.props;
    let iconName = rest.checked ? 'checkbox-checked' : 'checkbox-unchecked';
    const cls = cx(
      'core-checkbox',
      className,
      {
        checked: rest.checked || indeterminate,
        indeterminate,
        disabled: rest.disabled,
        'has-errors': errorMessage || warningMessage || successMessage || infoMessage,
      }
    );

    return (
      <span className={cls}>
        <label htmlFor={id}>
          <input {...rest} id={id} type="checkbox" onChange={this.onChange}/>
          <Icon name={indeterminate ? 'checkbox-indeterminate' : iconName} className="icon"/>
          {label && <span className="title">{label}</span>}
        </label>
        {children}
        {(errorMessage || warningMessage || successMessage || infoMessage) && (
          <Notice errorMessage={errorMessage} warningMessage={warningMessage} successMessage={successMessage} infoMessage={infoMessage}/>
        )}
      </span>
    );
  }

}
